// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import InvestingLoginScreenTemplate from '../TradingPlatformTemplates/InvestingLoginScreenTemplate.html';
import { Connection } from '@shared/commons/Connection';
import { ConntionInfo } from '@shared/commons/ConnectionInfo';
import { ConnectionStates } from '@shared/utils/Connections/connection.types';
import { DataCache } from '@shared/commons/DataCache';
import { BrandingUtils } from '@shared/commons/UtilsClasses/BrandingUtils';
import { Resources } from '@shared/localizations/Resources';
import { ResourcesManager } from '@shared/commons/properties/ResourcesManager';
import { ScreensNames } from '@front/controls/UtilsClasses/FactoryConstants';
import { TerceraLoginScreen } from '@front/controls/screen/TerceraLoginScreen';
import { MessageBoxType } from '@shared/utils/Interfaces/Screens/MessageBox.types';
import { InvestingMessageBox } from '../Controls/elements/InvestingMessageBox';
import { Themes } from '@shared/utils/ThemesEnum';
export class InvestingLoginScreen extends TerceraLoginScreen {
    constructor() {
        super();
    }
    getType() {
        return ScreensNames.InvestingLoginScreen;
    }
    oninit() {
        super.oninit();
        ResourcesManager.onLocaleChange.Subscribe(this.localize.bind(this), this);
    }
    connectionStateChange() {
        super.connectionStateChange();
        const state = Connection.getState();
        switch (state) {
            case ConnectionStates.CONNECTED:
                void DataCache.ProcessLogin(super.get('connectionParams.login')).then(() => {
                    DataCache.RecalculateBalances();
                    super.fire('loginBtnEventClick');
                });
                break;
            case ConnectionStates.DISCONNECTED:
                const onDisconnect = super.get('onDisconnectHandler');
                if (onDisconnect != null) {
                    onDisconnect();
                }
                const err = Connection.getLocalizedConnectionErrorString(super.get('connectionParams.login'));
                if (err != null) {
                    void super.set({ hasError: true });
                }
                break;
        }
    }
    oncomplete() {
        // NO BASE CALLING
        this.connectionStateChange = this.connectionStateChange.bind(this);
        const loginTextBox = this.Controls.tbLogin;
        const passTextBox = this.Controls.tbPwd;
        loginTextBox.on('keyDown', super.onLoginKeyDown.bind(this));
        passTextBox.on('keyDown', super.onPasswordKeyDown.bind(this));
        loginTextBox.getInputElement().addEventListener('input', super.trimStringIfWasPaste.bind(this));
        passTextBox.getInputElement().addEventListener('input', super.trimStringIfWasPaste.bind(this));
        Connection.subscribeOnConnectStateChange(this.connectionStateChange);
        this.updateConnectionInfo();
        ConntionInfo.OnConnectionInfoArrayChanged.Subscribe(this.updateConnectionInfo.bind(this), this);
    }
    updateConnectionInfo() {
        void super.set('connectionInfo', ConntionInfo.GetConnectionInfoArray());
    }
    onRecoverClicked() {
        const connectionParams = super.get('connectionParams');
        const forgotURL = connectionParams.forgotURL;
        if (forgotURL === '') {
            void super.set('showForgotScreen', true);
        }
        else {
            window.open(forgotURL);
        }
    }
    startLogin(context, verificationPassword, isExploreMode) {
        void super.set({ hasError: false }).then(() => {
            super.startLogin(context, verificationPassword, isExploreMode);
        });
    }
    get ChangePasswordScreen() {
        return this.Controls.invChangePassScr;
    }
    showChangePwdScreen(UserID = '', OldPass, cancelCB, isTradingPassword = false) {
        const changePasswordScreen = this.ChangePasswordScreen;
        if (changePasswordScreen != null) {
            changePasswordScreen.clearFields();
            changePasswordScreen.hidePasswords();
            changePasswordScreen.setScreenData(UserID, OldPass, cancelCB, isTradingPassword);
        }
    }
    dispose() {
        ResourcesManager.onLocaleChange.UnSubscribe(this.localize.bind(this), this);
        ConntionInfo.OnConnectionInfoArrayChanged.UnSubscribe(this.updateConnectionInfo.bind(this), this);
        super.dispose();
    }
    localize() {
        void super.set({
            forgotPasswordLabel: Resources.getResource('screen.LoginScreen.ForgotLinkLabel'),
            loginBtnTxt: Resources.getResource('screen.LoginScreen.LoginButtonText'),
            savePassText: Resources.getResource('screen.LoginScreen.SavePassCheckBox'),
            textExplore: Resources.getResource('screen.LoginScreen.ExploreLinkLabel'),
            tooltipExplore: Resources.getResource('screen.LoginScreen.ExploreLinkLabel.ToolTip')
        });
    }
    SetWebLoginBanner() {
        var _a;
        void super.set({
            loginLogoSrc: (_a = BrandingUtils.GetWebLoginBanner(Themes.Light)) !== null && _a !== void 0 ? _a : 'assets/webdesktop/resources/LogoTradingPlatform.svg'
        });
    }
    SetSplashScreen(connData) {
        const propName = 'BrandingInvestingSplashScreen';
        const cData = connData != null ? connData[propName] : null;
        if (cData == null) {
            return;
        }
        const splashSrc = BrandingUtils.GetSplashScreen();
        void super.set('splashImgSrc', splashSrc);
    }
    openErrorMessageScreen(errorText) {
        void this.set({ textErrorLabel: '', isOpenErrorMessageScreen: true });
        const callBack = () => {
            this.closeErrorMessageScreen();
        };
        InvestingMessageBox.Show(null, errorText, MessageBoxType.Warning, callBack, callBack, false, true, null, {
            style_addition_header: 'message-box-header',
            okText: Resources.getResource('general.messageBox.ok')
        }, true);
    }
}
TerceraLoginScreen.extendWith(InvestingLoginScreen, {
    template: InvestingLoginScreenTemplate,
    data: function () {
        return {
            splashImgSrc: null,
            logo_login_style: 'logo_login',
            loginLogoSrc: 'assets/webdesktop/resources/investing_loginscreen_logo_te.svg',
            onDisconnectHandler: null,
            showForgotScreen: false, // is InvestingForgotPasswordScreen visible
            showChangeScreen: false, // is InvestingChangePasswordScreen visible
            textErrorLabel: '',
            hasError: false
        };
    }
});
